import React, { Fragment } from 'react';

import Image from 'gatsby-image';
import classNames from 'classnames';
import injectSheet from 'react-jss';
import { Element } from 'react-scroll';
import _find from 'lodash/find';

import SPAPage from '../SPAPage';
import { Block, Row, AnimateOnVisible } from '../common';
import ImagePicker from '../ImagePicker';
import CTA from '../CTA';
import Header, { headerHeight } from '../Header';
import theme, { mediaQuerySmallScreen } from '../../theme';

import { getDeetAnchor } from '../../modules/flavours';
import useClientWindowSize from '../../hooks/useClientWindowSize';
import useClientWindowScroll from '../../hooks/useClientWindowScroll';

const styles = {
  deetBlockLeft: {
    marginRight: theme.margins.small,
  },
  deetBlockRight: {
    marginLeft: theme.margins.small,
  },
  primaryDeet: {
    padding: theme.margins.large,
  },
  fullHeightLargeScreens: {
    height: '100vh',
  },
  [mediaQuerySmallScreen]: {
    primaryDeet: {
      paddingTop: headerHeight + theme.margins.medium,
    },
    fullHeightLargeScreens: {
      height: 'auto',
    },
    deetBlock: {
      marginRight: 0,
      marginLeft: 0,
      marginBottom: theme.margins.medium,
    },
  },
};

const LandingPage = ({
  classes,
  orderedHooks,
  orderedDetail,
  activeHook,
  onSetActiveHook,
  prop,
}) => {
  const { width, height } = useClientWindowSize();
  const { scrollY } = useClientWindowScroll();

  // defaults for server rendering
  let isHeaderSticky = false;

  // header should be fixed at the top of the screen on small screen devices
  // or when the scroll has passed the sticky point. Must fix height of first
  // 'page' when header is floating at bottom of screen.
  if (!!width && !!height) {
    const hasFloatingHeader = width > theme.media.breakpoints.tablet;
    isHeaderSticky = hasFloatingHeader ? scrollY > height - headerHeight : true;
  }
  const activeHookOrDefault = activeHook
    ? _find(orderedHooks.hooks, ['id', activeHook.id])
    : orderedHooks.hooks[0];

  return (
    <Fragment>
      {/* Hooks and main Prop */}
      <Element key="home" name="home">
        <SPAPage className={classes.fullHeightLargeScreens}>
          <Row className={classNames(classes.primaryDeet)} responsive>
            <Block
              className={classNames(classes.deetBlock, classes.deetBlockLeft)}
            >
              <ImagePicker
                thumbnails={orderedHooks.hooks.map(hook => ({
                  ...hook.image,
                  id: hook.id,
                }))}
                selectedImage={{
                  ...activeHookOrDefault.image,
                  id: activeHookOrDefault.id,
                  descriptionHtml:
                    activeHookOrDefault.longText.childContentfulRichText.html,
                }}
                onSelectImage={onSetActiveHook}
              />
            </Block>
            <Block
              className={classNames(classes.deetBlock, classes.deetBlockRight)}
            >
              <CTA
                buttonText={prop.snippetText}
                image={{
                  ...prop.image,
                  id: prop.id,
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: prop.longText.childContentfulRichText.html,
                  }}
                />
              </CTA>
            </Block>
          </Row>

          <Header
            headerLinks={orderedDetail.deets.map(d => ({
              ...d,
              anchor: getDeetAnchor(d),
            }))}
            isSticky={isHeaderSticky}
          />
        </SPAPage>
      </Element>

      {/* Deets */}
      {orderedDetail.deets.map((deet, index) => {
        const isReversed = index % 2 === 0;
        return (
          <Element key={deet.id} name={getDeetAnchor(deet)}>
            <SPAPage
              key={deet.id}
              anchor={deet.id}
              style={{
                paddingTop: headerHeight + theme.margins.medium,
                paddingRight: theme.margins.medium,
                paddingBottom: theme.margins.medium,
                paddingLeft: theme.margins.medium,
              }}
            >
              <Row responsive reverse={isReversed}>
                <Block
                  className={classNames(
                    classes.deetBlock,
                    !isReversed ? classes.deetBlockLeft : classes.deetBlockRight
                  )}
                >
                  <h2>{deet.title}</h2>
                  {!!(
                    deet.mainBody &&
                    deet.mainBody.childContentfulRichText &&
                    deet.mainBody.childContentfulRichText.html
                  ) && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: deet.mainBody.childContentfulRichText.html,
                      }}
                    />
                  )}
                  {!!deet.prop && (
                    <Block
                      style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <CTA buttonText={deet.prop.snippetText} />
                    </Block>
                  )}
                </Block>
                <Block
                  className={classNames(
                    classes.deetBlock,
                    !isReversed ? classes.deetBlockRight : classes.deetBlockLeft
                  )}
                >
                  <AnimateOnVisible direction={isReversed ? 'left' : 'right'}>
                    {deet.images.map(i => (
                      <Image
                        key={i.id}
                        fluid={i.fluid}
                        style={{
                          height: '100%',
                        }}
                      />
                    ))}
                  </AnimateOnVisible>
                </Block>
              </Row>
            </SPAPage>
          </Element>
        );
      })}
    </Fragment>
  );
};

const LandingPageStyled = injectSheet(styles)(LandingPage);
export default LandingPageStyled;
